import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    isWishListLineDeactivated,
    isWishListLineDiscontinued,
    isWishListLineRestricted,
} from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import setWishListLineIsSelected from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetWishListLineIsSelected";
import updateQuantity from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateQuantity";
import updateUnitOfMeasure from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateUnitOfMeasure";
import updateWishListLineQuantities from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateWishListLineQuantities";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityMessageType,
    ProductModel,
    WishListLineModel,
    WishListModel,
} from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges, { ProductImageBadgesStyles } from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges, { ProductTextBadgesStyles } from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import MyListDetailsDiscontinuedProductListLine from "@insite/content-library/Widgets/MyListsDetails/MyListDetailsDiscontinuedProductListLine";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import Clickable from "@insite/mobius/Clickable";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import RefreshCw from "@insite/mobius/Icons/RefreshCw";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu from "@insite/mobius/OverflowMenu";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { Color } from "@insite/mobius/Swatch";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { comingSoonDiscontinueDates } from "@tKgpco/Common/commonFunctions";
import SeeMoreInfoModal from "@tKgpco/Components/SeeMoreInfoModal";
import { changeCustomerSelectCustomerContainerStyles as buttonStyle } from "@tKgpco/Overrides/Widgets/ChangeCustomer/ChangeCustomerSelectCustomerContainer";
import { COLORS, TYPOGRAPHY } from "@tKgpco/theme/design";
import cloneDeep from "lodash/cloneDeep";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    wishList: WishListModel;
    wishListLine: WishListLineModel;
    productInfo: ProductInfo;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    showImageBadges: boolean;
    maximumNumberOfImageBadges: number;
    showTextBadges: boolean;
    maximumNumberOfTextBadges: number;
    onViewReplacementProduct?: (wishListLine: WishListLineModel) => void;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    isSelected: !!state.pages.myListDetails.selectedWishListLineIds.find(o => o === ownProps.wishListLine.id),
    settingsCollection: getSettingsCollection(state),
    isQuantityUpdated: state.pages.myListDetails.wishListLinesWithUpdatedQuantity[ownProps.wishListLine.id],
    isAuthenticated: state.context.session?.isAuthenticated && !state.context.session?.isGuest,
    properties: state.context.settings.properties,
});

const mapDispatchToProps = {
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    updateWishListLineQuantities,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MyListsDetailsProductListLineStyles {
    editNotesLink?: LinkPresentationProps;
    lineInnerContainer?: GridContainerProps;
    selectedCheckboxHidden?: HiddenProps;
    imageGridItem?: GridItemProps;
    imageGridItemWrapper?: GridItemProps;
    productDetailsMainWrapper?: GridItemProps;
    selectedCheckbox?: CheckboxPresentationProps;
    imageWrapper?: InjectableCss;
    editNotesHidden?: HiddenProps;
    productImageStyles?: ProductImageStyles;
    descriptionGridItem?: GridItemProps;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    notesStyles?: SmallHeadingAndTextStyles;
    overflowMenuGridItem?: GridItemProps;
    partNumbersHidden?: HiddenProps;
    partNumbersGridItem?: GridItemProps;
    partNumbersGridItemMobile?: GridItemProps;
    priceAndAvailabilityGridItem?: GridItemProps;
    priceAndAvailabilityInnerContainer?: GridContainerProps;
    purchasedDateGridItem?: GridItemProps;
    purchasedDateLabelText?: TypographyPresentationProps;
    purchasedDateText?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    productPriceStyles?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    uomAndQuantityGridItem?: GridItemProps;
    uomAndQuantityInnerContainer?: GridContainerProps;
    uomGridItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    quantityGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    availabilityGridItem?: GridItemProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    notesGridItemMobileView?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    addToCartButton?: ButtonPresentationProps;
    deleteLinkHidden?: HiddenProps;
    deleteLink?: LinkPresentationProps;
    updateQuantityWrapper?: InjectableCss;
    updateQuantityLink?: LinkPresentationProps;
    quantityUpdatedIcon?: IconPresentationProps;
    quantityUpdatedText?: TypographyPresentationProps;
    restrictedDescriptionGridItem?: GridItemProps;
    restrictedDescriptionText?: TypographyPresentationProps;
    restrictedMessageText?: TypographyPresentationProps;
    restrictedRemoveItemLink?: LinkPresentationProps;
    textBadgeStyles?: ProductTextBadgesStyles;
    imageBadgeStyles?: ProductImageBadgesStyles;
    seeLeadTimeLink?: LinkPresentationProps;
    seeLeadTimeLinkGridItem?: GridItemProps;
    tooltipText?: InjectableCss;
    badgeImgWrapper?: InjectableCss;
    productImageWrapper?: InjectableCss;
    notFoundLabel?: TypographyPresentationProps;
    tooltiptext2?: InjectableCss;
    text?: InjectableCss;
    errMSg?: InjectableCss;
    salesBlock?: InjectableCss;
}

export const productListLineStyles: MyListsDetailsProductListLineStyles = {
    editNotesLink: {
        typographyProps: { size: 12 },
        css: css`
            margin-top: 5px;
        `,
    },
    lineInnerContainer: {
        gap: 10,
    },
    selectedCheckboxHidden: { below: "md" },
    imageGridItem: {
        width: 12,
        printWidth: 1,
    },
    imageGridItemWrapper: {
        width: [3, 5, 2, 2, 2],
        // printWidth: 1,
    },
    productDetailsMainWrapper: {
        width: [9, 7, 10, 10, 10],
        // printWidth: 1,
        css: css`
            @media print {
                min-width: 82%;
            }
        `,
    },
    selectedCheckbox: {
        sizeVariant: "small",
        css: css`
            @media print {
                display: none;
            }
            margin-right: 10px;
            /*background:red;
            & > span {
                background: #fff;
                width: 16px;
                height: 16px;
                border-radius: 3px;
                border: 1px solid #e2e3e6;
                & :focus {
                    outline: none;
                    box-shadow: 0 0 0 1px #e2e3e6;
                }
            }*/
            span[role="checkbox"] {
                width: 16px;
                height: 16px;
                justify-content: center;
            }
            span[role="checkbox"][aria-checked="true"]:not([aria-disabled="true"]) {
                background: #fff;
                border: 1px solid #bebdbd;
                color: #19a3e0;
                width: 16px;
                height: 16px;
                justify-content: center;
            }
            span[role="checkbox"]:focus {
                outline: none;
                box-shadow: 0 0 0 1px #e2e3e6;
                width: 16px;
                height: 16px;
                justify-content: center;
            }
        `,
    },
    imageWrapper: {
        css: css`
            @media print {
                min-height: 83px;
                min-width: 83px;
            }
            width: 100%;
            min-width: 0;
            flex-direction: column;
            position: relative;
        `,
    },
    editNotesHidden: { below: "md" },
    descriptionGridItem: {
        width: [10, 10, 10, 6, 6],
        printWidth: 6,
        css: css`
            flex-direction: column;
            padding: 15px 0px 0px 20px;
        `,
    },
    productPartNumbersStyles: {
        erpNumberGridItem: {
            css: css`
                flex-direction: column;
                flex-direction: row;
                align-items: baseline;
            `,
        },
        // erpNumberLabelText: { variant: "legend" },
        erpNumberLabelText: {
            variant: "h4",
            color: COLORS.lightBlack,
            css: css`
                margin-right: 5px;
            `,
        },
        erpNumberValueText: { size: TYPOGRAPHY.size.smaller, weight: TYPOGRAPHY.weight.base, color: COLORS.lightBlack },
        customerNameGridItem: {
            css: css`
                flex-direction: column;
                flex-direction: row;
                align-items: baseline;
            `,
        },
        // customerNameLabelText: { variant: "legend" },
        customerNameLabelText: { variant: "h4", color: COLORS.lightBlack },
        customerNameValueText: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
        manufacturerItemGridItem: {
            css: css`
                flex-direction: row;
                align-items: baseline;
            `,
        },
        manufacturerItemLabelText: { variant: "h4", color: COLORS.lightBlack },
        manufacturerItemValueText: {
            size: TYPOGRAPHY.size.smaller,
            weight: TYPOGRAPHY.weight.base,
            color: COLORS.lightBlack,
        },
    },
    overflowMenuGridItem: {
        width: [2, 1, 0, 0, 0],
        printWidth: 0,
        css: css`
            padding: 15px 0px 0px 15px;
            button[data-test-selector="popoverOverflowTrigger"] {
                min-width: auto;
            }
            button {
                &:disabled {
                    color: ${COLORS.disabled.base} !important;
                }
            }
        `,
    },
    partNumbersHidden: {
        below: "md",
        css: css`
            @media print {
                display: block;
            }
            width: 100%;
            & > div {
                padding: 5px 0 !important;
            }
        `,
    },
    partNumbersGridItem: {
        width: 12,
        css: css`
            flex-direction: column;

            > span {
                font-size: 14px;
                font-weight: 400;
                color: #353432;
            }
        `,
    },
    partNumbersGridItemMobile: {
        width: 12,
        css: css`
            padding: 5px;
            flex-direction: column;
            > span {
                font-size: 14px;
                font-weight: 400;
                color: #353432;
            }
        `,
    },
    priceAndAvailabilityGridItem: {
        width: [12, 12, 4, 4, 5],
        printWidth: 5,
        css: css`
            ${({ theme }) => {
                const { maxWidths } = theme.breakpoints || baseTheme.breakpoints;
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 5px;
                    `,
                    css`
                        padding: 15px 0px 0px 5px;
                    `,
                    css`
                        padding: 15px 0px 0px 5px;
                    `,
                ]);
            }}
        `,
    },
    priceAndAvailabilityInnerContainer: { gap: 10 },
    purchasedDateGridItem: {
        width: [12, 12, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    purchasedDateLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
        `,
    },
    priceGridItem: {
        width: [12, 6, 12, 12, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
            span[data-test-selector^="productPrice_unitNetPrice"] {
                color: rgb(30, 117, 186);
            }
        `,
    },
    productPriceStyles: {
        priceLabelText: {
            variant: "p",
            transform: "uppercase",
            weight: 600,
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 0.25rem;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: { typographyProps: { size: 12 } },
    },
    uomAndQuantityGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 7,
    },
    uomAndQuantityInnerContainer: {
        gap: 10,
        css: css`
            @media print {
                input,
                select {
                    height: 18px !important;
                    font-size: 11px !important;
                }
            }
        `,
    },
    uomGridItem: {
        width: [12, 6, 9, 9, 9],
        printWidth: 6,
        css: css`
            & div {
                margin-top: 0px;
                flex-direction: row;
                align-items: center;
                border-radius: 8px;
                background-color: #e1e7ed;
                color: ${COLORS.lightBlack};
                label {
                    text-align: center;
                    padding: 8px;
                    width: 40%;
                    @media (max-width: 1199px) and (min-width: 200px) {
                        font-size: 13px !important;
                    }
                }
                & > div {
                    margin-top: 0 !important;
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                    }
                    select {
                        border-radius: 0 8px 8px 0 !important;
                    }
                }
            }
        `,
    },
    quantityGridItem: {
        width: [12, 6, 9, 9, 9],
        printWidth: 6,
        css: css`
            & div {
                margin-top: 0px;
                flex-direction: row;
                align-items: center;
                border-radius: 8px;
                background-color: #e1e7ed;
                color: ${COLORS.lightBlack};
                label {
                    text-align: center;
                    padding: 8px;
                    width: 40%;
                    @media (max-width: 1199px) and (min-width: 200px) {
                        font-size: 13px !important;
                    }
                    @media print {
                        display: block !important;
                        min-width: 46px !important;
                    }
                }
                & > div {
                    margin-top: 0 !important;
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 13px !important;
                    }
                    input {
                        border-radius: 0 8px 8px 0 !important;
                    }
                }
            }
        `,
    },
    productQuantityOrderedStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                    display: block !important;
                    width: 100%;
                }
            `,
        },
        cssOverrides: {
            inputSelect: css`
                @media print {
                    width: 100px;
                    display: block !important;
                }
            `,
        },
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: 12px;
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    availabilityGridItem: {
        width: [12, 6, 9, 9, 9],
        css: css`
            & > div {
                div {
                    & > :nth-child(2) {
                        justify-content: flex-end !important;
                    }
                }
            }
        `,
    },
    buttonsGridItem: {
        width: [12, 6, 2, 2, 2],
        printWidth: 0,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    notesGridItemMobileView: {
        width: 12,
        css: css`
            padding: 5px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 100%;
        `,
    },
    deleteLinkHidden: {
        below: "md",
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;
            align-items: center;
        `,
    },
    deleteLink: {
        typographyProps: { size: 16 },
        css: css`
            margin-top: 10px;
        `,
    },
    updateQuantityWrapper: {
        css: css`
            display: flex;
            margin-top: 10px;
        `,
    },
    updateQuantityLink: {
        typographyProps: { size: 16 },
        icon: {
            iconProps: {
                src: RefreshCw,
                size: 12,
            },
        },
    },
    quantityUpdatedIcon: {
        src: Check,
        size: 16,
        css: css`
            margin-right: 5px;
        `,
    },
    quantityUpdatedText: {
        size: 16,
    },
    restrictedDescriptionGridItem: {
        width: [12, 12, 6, 6, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    restrictedMessageText: { color: "warning" },
    textBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
    },
    imageBadgeStyles: {
        container: {
            css: css`
                width: 60%;
                margin-bottom: 5px;
            `,
        },
        imageItem: {
            css: css`
                margin: 5px 0;
            `,
        },
    },
    seeLeadTimeLink: {
        css: css`
            & :hover .tooltipText {
                visibility: visible;
            }
        `,
    },
    seeLeadTimeLinkGridItem: {
        width: 5,
        css: css`
            margin: 10px 0px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
            > span {
                background: #e1e7ed;
                padding: 3px 4px;
                border-radius: 4px;
                > span {
                    color: #353432 !important;
                    font-size: ${TYPOGRAPHY.size.vTiny};
                    @media (max-width: 1199px) and (min-width: 992px) {
                        font-size: 10px !important;
                    }
                }
            }
        `,
    },
    tooltipText: {
        css: css`
            visibility: hidden;
            width: auto;
            background-color: black;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 6px;
            opacity: 0.8;
            position: absolute;
            z-index: 1;
            margin-top: 10px;
        `,
    },
    badgeImgWrapper: {
        css: css`
            position: absolute;
            left: 50%;
            top: 50%;
            width: 65%;
            height: 65%;
            transform: translate(-50%, -50%);
            pointer-events: none;
        `,
    },
    productImageWrapper: {
        css: css`
            position: relative;
        `,
    },
    notFoundLabel: {
        size: 14,
        weight: 700,
    },
    text: {
        css: css`
            position: relative;
            font-size: 14px;
            font-weight: 600;
            color: #e6b23d;
            border-radius: 4px;
            display: inline-block;
            .cfp-msg span {
                color: #e6b23d !important;
            }
            :hover [data-test-selector="tooltiptext2"] {
                visibility: visible;
            }
            button {
                &:disabled {
                    span {
                        color: ${COLORS.disabled.base} !important;
                    }
                }
            }
        `,
    },
    tooltiptext2: {
        css: css`
            width: max-content;
            max-width: 180px;
            visibility: hidden;
            background: #b3b3b3;
            color: #fff;
            text-align: center;
            border-radius: 6px;
            padding: 5px 15px;
            font-weight: 400;
            position: absolute;
            z-index: 1;
            bottom: 100%;
            left: 50%;
            margin-left: -50px;
            margin-bottom: -8px;
            &::after {
                content: "";
                position: absolute;
                top: 100%;
                left: 23%;
                margin-left: 15px;
                border-width: 6px;
                border-style: solid;
                border-color: #b3b3b3 transparent transparent transparent;
            }
            span {
                color: #0645ad !important;
            }
        `,
    },
    errMSg: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            color: #fc3939;
        `,
    },

    salesBlock: {
        css: css`
            border: 2px solid;
            border-radius: 4px;
            padding: 5px;
            color: #f0b003;
            margin: 10px 0 3px;
            font-weight: bold;
            font-size: 14px;
            width: max-content;
        `,
    },
};

const styles = productListLineStyles;

const MyListsDetailsProductListLine: React.FC<Props> = ({
    wishList,
    wishListLine,
    productInfo,
    isSelected,
    settingsCollection,
    isQuantityUpdated,
    onDeleteClick,
    onEditNotesClick,
    updateWishListLineQuantities,
    setWishListLineIsSelected,
    updateUnitOfMeasure,
    updateQuantity,
    showImageBadges,
    showTextBadges,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    onViewReplacementProduct,
    isAuthenticated,
    properties,
}) => {
    // eslint-disable-next-line no-use-before-define
    const product = mapWishListLineToProduct(wishListLine);

    const selectChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setWishListLineIsSelected({ wishListLineId: wishListLine.id, isSelected: value });
    };

    const onUpdateQuantityClick = () => {
        updateWishListLineQuantities({ reloadWishListLines: true });
    };

    const isDiscontinued = () => {
        const availability = productInfo.inventory?.inventoryAvailabilityDtos?.find(
            o => o.unitOfMeasure.toLowerCase() === productInfo.unitOfMeasure.toLowerCase(),
        )?.availability;

        return (
            !wishListLine.isActive ||
            (wishListLine.isDiscontinued && availability?.messageType === AvailabilityMessageType.OutOfStock)
        );
    };

    const isRestricted = () => {
        if (isDiscontinued()) {
            return false;
        }

        return !wishListLine.isVisible;
    };

    const canEditWishList = (wishList.allowEdit || !wishList.isSharedList) && !wishList.isAutogenerated;

    const { productSettings } = settingsCollection;
    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: qtyOrdered => {
            updateQuantity({ wishListLineId: wishListLine.id, qtyOrdered });
        },
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateUnitOfMeasure({ wishListLineId: wishListLine.id, unitOfMeasure });
        },
    };

    if (isWishListLineDiscontinued(wishListLine, productInfo) && wishListLine.isActive) {
        return (
            <MyListDetailsDiscontinuedProductListLine
                wishList={wishList}
                wishListLine={wishListLine}
                productContext={productContext}
                isSelected={isSelected}
                selectChangeHandler={selectChangeHandler}
                onDeleteClick={onDeleteClick}
                onViewReplacementProduct={onViewReplacementProduct}
            />
        );
    }

    if (isWishListLineRestricted(wishListLine, productInfo) || isWishListLineDeactivated(wishListLine)) {
        // if (isRestricted() || isDiscontinued()) {
        return (
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-restricted-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem
                    {...styles.restrictedDescriptionGridItem}
                    className="wishlist-restricted-line-description-grid-item"
                >
                    <Typography
                        {...styles.restrictedDescriptionText}
                        className="wishlist-restricted-line-description-text"
                    >
                        {wishListLine.shortDescription}
                    </Typography>
                    <Hidden {...styles.partNumbersHidden}>
                        <ProductPartNumbers
                            productNumber={product.productNumber}
                            customerProductNumber={product.customerProductNumber}
                            manufacturerItem={product.manufacturerItem}
                            extendedStyles={styles.productPartNumbersStyles}
                        />
                    </Hidden>
                </GridItem>
                <GridItem {...styles.partNumbersGridItem} className="wishlist-restricted-line-part-number-grid-item">
                    <ProductPartNumbers
                        productNumber={product.productNumber}
                        customerProductNumber={product.customerProductNumber}
                        manufacturerItem={product.manufacturerItem}
                        extendedStyles={styles.productPartNumbersStyles}
                    />
                </GridItem>
                <GridItem {...styles.priceAndAvailabilityGridItem}>
                    <Typography
                        {...styles.restrictedMessageText}
                        className="wishlist-restricted-line-message-text"
                        data-test-selector="restrictedMessage"
                    >
                        {isWishListLineRestricted(wishListLine, productInfo) &&
                            !isWishListLineDeactivated(wishListLine) && (
                                <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>
                            )}
                        {isWishListLineDeactivated(wishListLine) && (
                            <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Deactivation")}</>
                        )}
                    </Typography>
                    <Typography {...styles.restrictedMessageText} data-test-selector="restrictedMessage">
                        {isRestricted() && <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Restrictions")}</>}
                        {isDiscontinued() && <>{siteMessage("Lists_Item_Not_Displayed_Due_To_Discontinued")}</>}
                    </Typography>
                </GridItem>
                <GridItem {...styles.buttonsGridItem} className="wishlist-restricted-line-buttons-grid-item">
                    {canEditWishList && (
                        <Link
                            className="wishlist-restricted-line-remove-link"
                            {...styles.restrictedRemoveItemLink}
                            onClick={() => onDeleteClick(wishListLine)}
                            data-test-selector="removeRestricted"
                        >
                            {translate("Remove item")}
                        </Link>
                    )}
                </GridItem>
            </GridContainer>
        );
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    const isProductQuantityUpdate = wishList?.properties?.productQuantityUpdate;
    const kgpManaged = wishList?.properties?.listType === "KGPManaged";
    const minimumOrderQty = Number(wishListLine.properties.minimumOrderQty) || 0;
    const multipleSaleQty = Number(wishListLine.properties.multipleSaleQty) || 0;
    const isSalesBlockItem = wishListLine.properties?.salesBlocked?.toLowerCase() === "true";
    const { comingSoonImagePath, discontinuedImagePath } = properties;
    const isComingSoon = comingSoonDiscontinueDates(wishListLine).isComingSoon;
    const isDiscontinuedFlag = comingSoonDiscontinueDates(wishListLine).isDiscontinued;
    let leadtime = "";
    if (window.localStorage.getItem("customInventory")) {
        const inventory = JSON.parse(window.localStorage.getItem("customInventory") || "null");
        if (inventory && inventory.length > 0) {
            const filterInventory = inventory.filter((data: any) => data.productId === wishListLine.productId);
            if (filterInventory.length > 0) {
                leadtime = filterInventory[0].additionalResults?.leadTime ?? "";
            }
        }
    }
    return (
        <ProductContext.Provider value={productContext}>
            <GridContainer
                {...styles.lineInnerContainer}
                className="wishlist-line-grid-container"
                data-test-selector={`${wishListLine.productId}_${wishListLine.selectedUnitOfMeasure}`}
            >
                <GridItem {...styles.imageGridItemWrapper}>
                    <GridContainer>
                        <GridItem {...styles.imageGridItem}>
                            <Hidden {...styles.selectedCheckboxHidden}>
                                <Checkbox
                                    {...styles.selectedCheckbox}
                                    disabled={isSalesBlockItem}
                                    checked={isSelected}
                                    onChange={selectChangeHandler}
                                    hiddenIconText={translate("Select")}
                                    data-test-selector="myListDetails_selectRowCheckbox"
                                />
                            </Hidden>
                            <StyledWrapper {...styles.imageWrapper} className="wishlist-line-product-image-wrapper">
                                <StyledWrapper {...styles.productImageWrapper}>
                                    <ProductImage product={productContext} extendedStyles={styles.productImageStyles} />
                                    {isAuthenticated && isComingSoon && (
                                        <StyledWrapper className="coming-soon" {...styles.badgeImgWrapper}>
                                            <LazyImage src={comingSoonImagePath} />
                                        </StyledWrapper>
                                    )}
                                    {isAuthenticated && isDiscontinuedFlag && (
                                        <StyledWrapper className="discontinued" {...styles.badgeImgWrapper}>
                                            <LazyImage src={discontinuedImagePath} />
                                        </StyledWrapper>
                                    )}
                                </StyledWrapper>
                                {canEditWishList && (
                                    <Hidden {...styles.editNotesHidden}>
                                        <Link
                                            {...styles.editNotesLink}
                                            className="wishlist-line-edit-notes-link"
                                            onClick={() => onEditNotesClick(wishListLine)}
                                            data-test-selector="myListDetails_addOrEditNote"
                                        >
                                            {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                        </Link>
                                    </Hidden>
                                )}
                            </StyledWrapper>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                <GridItem {...styles.productDetailsMainWrapper}>
                    <GridContainer>
                        <GridItem {...styles.descriptionGridItem} className="wishlist-line-description-grid-item">
                            {product.brand && (
                                <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                            )}
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                            <Hidden {...styles.partNumbersHidden}>
                                <GridItem
                                    {...styles.partNumbersGridItem}
                                    className="wishlist-line-product-part-number-grid-item"
                                >
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                    {product?.properties?.longDescription && (
                                        <Typography>{product.properties.longDescription}</Typography>
                                    )}
                                </GridItem>
                                {wishListLine.notes && (
                                    <GridItem {...styles.notesGridItem} className="wishlist-line-notes-grid-item">
                                        <SmallHeadingAndText
                                            heading={translate("Notes")}
                                            text={wishListLine.notes}
                                            extendedStyles={styles.notesStyles}
                                        />
                                    </GridItem>
                                )}
                            </Hidden>
                            {isAuthenticated && <SeeMoreInfoModal product={wishListLine} />}
                            {showTextBadges && textBadges && (
                                <ProductTextBadges
                                    badgeList={textBadges}
                                    maxBadges={maximumNumberOfTextBadges ?? 4}
                                    extendedStyles={styles.textBadgeStyles}
                                />
                            )}
                            {showImageBadges && imageBadges && (
                                <ProductImageBadges
                                    badgeList={imageBadges}
                                    maxBadges={maximumNumberOfImageBadges ?? 4}
                                    extendedStyles={styles.imageBadgeStyles}
                                    numberOfColumns={4}
                                />
                            )}
                            {isAuthenticated && leadtime && leadtime !== "" && (
                                <GridItem {...styles.seeLeadTimeLinkGridItem}>
                                    <Link {...styles.seeLeadTimeLink} data-test-selector="seeLeadTimeLink">
                                        {translate("See Lead Time")}
                                        <StyledWrapper
                                            className="tooltipText"
                                            {...styles.tooltipText}
                                        >{`${leadtime} days`}</StyledWrapper>
                                    </Link>
                                </GridItem>
                            )}
                        </GridItem>
                        {canEditWishList && (
                            <GridItem {...styles.overflowMenuGridItem}>
                                <OverflowMenu position="end">
                                    <Clickable onClick={() => onEditNotesClick(wishListLine)}>
                                        {translate(`${wishListLine.notes ? "Edit" : "Add"} Notes`)}
                                    </Clickable>
                                    <Clickable disabled={kgpManaged} onClick={() => onDeleteClick(wishListLine)}>
                                        {translate("Delete")}
                                    </Clickable>
                                </OverflowMenu>
                            </GridItem>
                        )}
                        <Hidden {...styles.notesHidden}>
                            <GridItem {...styles.partNumbersGridItemMobile}>
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                                {product?.properties?.longDescription && (
                                    <Typography>{product.properties.longDescription}</Typography>
                                )}
                            </GridItem>
                        </Hidden>
                        <GridItem {...styles.priceAndAvailabilityGridItem}>
                            <GridContainer
                                {...styles.priceAndAvailabilityInnerContainer}
                                className="wishlist-line-price-availability-grid-container"
                            >
                                {wishList.isAutogenerated && (
                                    <GridItem
                                        {...styles.purchasedDateGridItem}
                                        className="wishlist-line-purchased-date-grid-container"
                                    >
                                        <Typography
                                            {...styles.purchasedDateLabelText}
                                            className="wishlist-line-purchased-date-label-text"
                                        >
                                            {translate("Purchased Date")}
                                        </Typography>
                                        <Typography
                                            {...styles.purchasedDateText}
                                            className="wishlist-line-purchased-date-text"
                                            data-test-selector="purchasedDate"
                                        >
                                            <LocalizedDateTime dateTime={wishListLine.createdOn} />
                                        </Typography>
                                    </GridItem>
                                )}
                                <GridItem {...styles.priceGridItem} className="wishlist-line-price-grid-container">
                                    <StyledWrapper>
                                        <Typography {...styles.notFoundLabel}>{translate("Your Price:")}</Typography>
                                    </StyledWrapper>
                                    <ProductPrice
                                        product={productContext}
                                        showSavings={true}
                                        showSavingsAmount={productSettings.showSavingsAmount}
                                        showSavingsPercent={productSettings.showSavingsPercent}
                                        extendedStyles={styles.productPriceStyles}
                                        showLabel={false}
                                    />
                                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                                </GridItem>
                                <GridItem {...styles.uomAndQuantityGridItem}>
                                    <GridContainer
                                        {...styles.uomAndQuantityInnerContainer}
                                        className="wishlist-line-uom-quantity-grid-container"
                                    >
                                        {product.unitOfMeasures && product.unitOfMeasures.length > 1 && (
                                            <GridItem {...styles.uomGridItem}>
                                                <ProductUnitOfMeasureSelect
                                                    disabled={!canEditWishList}
                                                    extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                                />
                                            </GridItem>
                                        )}
                                        <GridItem
                                            {...styles.quantityGridItem}
                                            className="wishlist-line-quantity-grid-item"
                                        >
                                            <ProductQuantityOrdered
                                                extendedStyles={styles.productQuantityOrderedStyles}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                {productSettings.showInventoryAvailability && (
                                    <GridItem
                                        {...styles.availabilityGridItem}
                                        className="wishlist-line-availability-grid-item"
                                    >
                                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                        {wishListLine.notes && (
                            <Hidden {...styles.notesHidden}>
                                <GridItem {...styles.notesGridItemMobileView} className="wishlist-line-notes-grid-item">
                                    <SmallHeadingAndText
                                        heading={translate("Notes")}
                                        text={wishListLine.notes}
                                        extendedStyles={styles.notesStyles}
                                    />
                                </GridItem>
                            </Hidden>
                        )}
                        <GridItem {...styles.buttonsGridItem} className="wishlist-line-buttons-grid-item">
                            {!(productInfo.qtyOrdered < minimumOrderQty) &&
                                !(
                                    !(productInfo.qtyOrdered <= minimumOrderQty) &&
                                    multipleSaleQty > 1 &&
                                    productInfo.qtyOrdered % multipleSaleQty !== 0
                                ) &&
                                productContext.productInfo &&
                                productContext.productInfo.pricing && (
                                    <ProductAddToCartButton
                                        notes={wishListLine.notes}
                                        // extendedStyles={styles.addToCartButton}
                                        extendedStyles={buttonStyle.continueButton}
                                        data-test-selector="addToCart"
                                    />
                                )}
                            {canEditWishList && (
                                <>
                                    <Hidden {...styles.deleteLinkHidden}>
                                        <StyledWrapper {...styles.text}>
                                            <Link
                                                disabled={kgpManaged}
                                                {...styles.deleteLink}
                                                className="wishlist-line-delete-link"
                                                onClick={() => onDeleteClick(wishListLine)}
                                                data-test-selector="deleteListItem"
                                            >
                                                {translate("Delete")}
                                            </Link>
                                            {kgpManaged && (
                                                <Typography {...styles.tooltiptext2} data-test-selector="tooltiptext2">
                                                    {siteMessage("KGP managed this item.")}
                                                </Typography>
                                            )}
                                        </StyledWrapper>
                                    </Hidden>
                                </>
                            )}
                            {(canEditWishList || isProductQuantityUpdate?.toLowerCase() === "true") && (
                                <StyledWrapper
                                    {...styles.updateQuantityWrapper}
                                    className="wishlist-line-update-quantity-wrapper"
                                >
                                    {isSalesBlockItem ? (
                                        <StyledWrapper {...styles.salesBlock}>
                                            {siteMessage("sales_block_product_msg")}
                                        </StyledWrapper>
                                    ) : null}
                                    {productInfo.qtyOrdered < minimumOrderQty ? (
                                        <StyledWrapper {...styles.errMSg}>{siteMessage("min_qty_msg")}</StyledWrapper>
                                    ) : !(productInfo.qtyOrdered <= minimumOrderQty) &&
                                      multipleSaleQty > 1 &&
                                      productInfo.qtyOrdered % multipleSaleQty !== 0 ? (
                                        <StyledWrapper {...styles.errMSg}>
                                            {siteMessage("multiple_qty_msg")}
                                        </StyledWrapper>
                                    ) : (
                                        productInfo.qtyOrdered > 0 &&
                                        wishListLine.qtyOrdered !== productInfo.qtyOrdered && (
                                            <Link
                                                {...styles.updateQuantityLink}
                                                className="wishlist-line-update-quantity-link"
                                                onClick={() => onUpdateQuantityClick()}
                                            >
                                                {translate("Update QTY")}
                                            </Link>
                                        )
                                    )}

                                    {isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered && (
                                        <>
                                            <Icon
                                                {...styles.quantityUpdatedIcon}
                                                className="wishlist-line-quantity-updated-icon"
                                            />
                                            <Typography
                                                {...styles.quantityUpdatedText}
                                                className="wishlist-line-quantity-updated-text"
                                            >
                                                {translate("QTY updated")}
                                            </Typography>
                                        </>
                                    )}
                                </StyledWrapper>
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </ProductContext.Provider>
    );
};

const mapWishListLineToProduct = (wishListLine: WishListLineModel) => {
    const product = cloneDeep(wishListLine) as any as ProductModel;
    product.id = wishListLine.productId;
    product.productNumber = wishListLine.erpNumber;
    product.productTitle = wishListLine.shortDescription;
    product.customerProductNumber = wishListLine?.customerName;
    product.unitOfMeasures =
        wishListLine?.productUnitOfMeasures?.map(u => ({
            id: u.productUnitOfMeasureId,
            ...u,
        })) || null;
    return product;
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsDetailsProductListLine);
